<script lang="ts">
import { onMount } from 'svelte';
import {
    getBuilderImageAspectVariantFileName,
    getBuilderImageAssetId,
} from '$lib/utils/builderImageUrl';
import {
    formatDirectBuilderImageSrcsetForAspectRatio,
    formatDirectBuilderImageUrlForAspectRatio,
} from '$lib/utils/formatImageUrl';
import { ETitleFontSizes } from '../../../config/enums';
import HeroText from '../HeroText/HeroText.svelte';

// Dynamic import for VideoPlayer to reduce initial bundle size
let VideoPlayerComponent:
    | typeof import('../../organisms/VideoPlayer/VideoPlayer.svelte').default
    | null = $state(null);
let isLoaded = $state(false);
let loadError = $state(false);
let isMobileViewport = $state(true);
let shouldRenderVideoPlayer = $state(false);
let isVideoReady = $state(false);

interface Props {
    muxId?: string;
    muxIdMobile?: string;
    poster?: string;
    posterMobile?: string;
    subContent?: string;
    mastheadTextColour?: string;
    content?: string;
    link?: string;
    linkText?: string;
    link2?: string;
    linkText2?: string;
    inverseButtonColours?: boolean;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    titleSize?: string;
}

let {
    muxId = '',
    muxIdMobile = '',
    poster = '',
    posterMobile = '',
    subContent = '',
    mastheadTextColour = '#000',
    content = '',
    link = '',
    linkText = 'Learn More',
    link2 = '',
    linkText2 = 'Learn More',
    inverseButtonColours = false,
    headingLevel = 'h1',
    titleSize = ETitleFontSizes.H2,
}: Props = $props();

const DESKTOP_POSTER_WIDTHS = [960, 1280, 1600, 1920] as const;
const MOBILE_POSTER_WIDTHS = [360, 480] as const;
const POSTER_PARAMS = 'format=webp&quality=42';
const DESKTOP_POSTER_ASPECT_RATIO = 19 / 8;
const MOBILE_POSTER_ASPECT_RATIO = 27 / 43;
const generatedPosterAssets = import.meta.glob('/static/generated/builder-images/*.webp', {
    eager: true,
    query: '?url',
    import: 'default',
}) as Record<string, string>;

const generatedPosterPath = (src: string, width: number, aspectRatio: number) => {
    const assetId = getBuilderImageAssetId(src);
    const fileName = assetId
        ? getBuilderImageAspectVariantFileName(assetId, width, aspectRatio)
        : '';
    const legacyFileName = assetId ? `${assetId}-${width}.webp` : '';
    const assetUrl =
        generatedPosterAssets[`/static/generated/builder-images/${fileName}`] ||
        generatedPosterAssets[`/static/generated/builder-images/${legacyFileName}`];

    return typeof assetUrl === 'string' ? assetUrl : '';
};

const formatPosterUrl = (src: string, width: number, aspectRatio: number) =>
    generatedPosterPath(src, width, aspectRatio) ||
    formatDirectBuilderImageUrlForAspectRatio(src, width, aspectRatio, POSTER_PARAMS);

const formatPosterSrcset = (src: string, widths: readonly number[], aspectRatio: number) => {
    const candidates = widths
        .map((width) => {
            const posterPath =
                generatedPosterPath(src, width, aspectRatio) ||
                formatDirectBuilderImageUrlForAspectRatio(src, width, aspectRatio, POSTER_PARAMS);

            return posterPath ? `${posterPath} ${width}w` : '';
        })
        .filter(Boolean)
        .join(', ');

    return (
        candidates ||
        formatDirectBuilderImageSrcsetForAspectRatio(src, widths, aspectRatio, POSTER_PARAMS)
    );
};

const desktopPosterSrc = $derived(formatPosterUrl(poster, 1280, DESKTOP_POSTER_ASPECT_RATIO));
const desktopPosterSrcset = $derived(
    formatPosterSrcset(poster, DESKTOP_POSTER_WIDTHS, DESKTOP_POSTER_ASPECT_RATIO),
);
const mobilePosterSrc = $derived(
    formatPosterUrl(posterMobile || poster, 480, MOBILE_POSTER_ASPECT_RATIO),
);
const mobilePosterSrcset = $derived(
    formatPosterSrcset(posterMobile || poster, MOBILE_POSTER_WIDTHS, MOBILE_POSTER_ASPECT_RATIO),
);
const activeMuxId = $derived(isMobileViewport && muxIdMobile ? muxIdMobile : muxId);
const activePoster = $derived(isMobileViewport ? mobilePosterSrc : desktopPosterSrc);
const shouldHidePoster = $derived(shouldRenderVideoPlayer && isVideoReady && !loadError);
const hiddenPosterAttribute = $derived(shouldHidePoster ? true : undefined);
const videoReadyAttribute = $derived(shouldHidePoster ? true : undefined);

const loadVideoPlayer = async () => {
    if (!muxId && !muxIdMobile) {
        isLoaded = true;
        return;
    }

    if (VideoPlayerComponent) {
        isVideoReady = false;
        shouldRenderVideoPlayer = true;
        return;
    }

    try {
        const module = await import('../../organisms/VideoPlayer/VideoPlayer.svelte');
        if (module?.default) {
            VideoPlayerComponent = module.default;
            isVideoReady = false;
            isLoaded = true;
            shouldRenderVideoPlayer = true;
        } else {
            console.error('VideoPlayer component module.default is undefined');
            VideoPlayerComponent = null;
            loadError = true;
            isLoaded = true;
        }
    } catch (error) {
        console.error('Failed to load VideoPlayer component:', error);
        VideoPlayerComponent = null;
        loadError = true;
        isLoaded = true;
    }
};

const markVideoReady = () => {
    isVideoReady = true;
};

onMount(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');

    const syncViewport = () => {
        isMobileViewport = mediaQuery.matches;
    };

    const shouldUseInteractionOnlyVideo = () => {
        const connection = (
            navigator as Navigator & {
                connection?: {
                    saveData?: boolean;
                    effectiveType?: string;
                    downlink?: number;
                    rtt?: number;
                };
            }
        ).connection;

        if (!connection) {
            return false;
        }

        return (
            connection.saveData === true ||
            ['slow-2g', '2g', '3g'].includes(connection.effectiveType ?? '') ||
            (typeof connection.downlink === 'number' &&
                connection.downlink > 0 &&
                connection.downlink < 1.5) ||
            (typeof connection.rtt === 'number' && connection.rtt > 400)
        );
    };

    const loadFromInteraction = () => {
        void loadVideoPlayer();
    };

    const configureLoadStrategy = () => {
        syncViewport();

        if (!muxId && !muxIdMobile) {
            isLoaded = true;
            return;
        }

        if (isMobileViewport) {
            shouldRenderVideoPlayer = false;
            return;
        }

        if (shouldUseInteractionOnlyVideo()) {
            shouldRenderVideoPlayer = false;
            return;
        }

        void loadVideoPlayer();
    };

    mediaQuery.addEventListener('change', configureLoadStrategy);
    window.addEventListener('pointerdown', loadFromInteraction, { once: true, passive: true });
    window.addEventListener('keydown', loadFromInteraction, { once: true });
    window.addEventListener('scroll', loadFromInteraction, { once: true, passive: true });

    configureLoadStrategy();

    return () => {
        mediaQuery.removeEventListener('change', configureLoadStrategy);
        window.removeEventListener('pointerdown', loadFromInteraction);
        window.removeEventListener('keydown', loadFromInteraction);
        window.removeEventListener('scroll', loadFromInteraction);
    };
});
</script>

<div class="herovideo" data-video-ready={videoReadyAttribute}>
    {#if shouldRenderVideoPlayer && isLoaded && VideoPlayerComponent && activeMuxId}
        {@const VideoPlayer = VideoPlayerComponent}
        <VideoPlayer
            muxId={activeMuxId}
            poster={activePoster}
            autoPlay={true}
            fitVideo={true}
            onReady={markVideoReady}
        />
    {/if}

    <div class="video-loading" data-hidden={hiddenPosterAttribute}>
        {#if poster || posterMobile}
            <picture>
                {#if desktopPosterSrc}
                    <source
                        media="(min-width: 768px)"
                        srcset={desktopPosterSrcset}
                        sizes="100vw"
                        type="image/webp"
                    />
                {/if}
                <img
                    src={mobilePosterSrc || desktopPosterSrc}
                    srcset={mobilePosterSrcset}
                    sizes="100vw"
                    alt=""
                    data-hero-poster="true"
                    fetchpriority="high"
                    decoding="async"
                />
            </picture>
        {:else if loadError}
            <div class="video-placeholder">Video unavailable</div>
        {:else if !shouldRenderVideoPlayer || !isVideoReady}
            <div class="video-placeholder">Loading video...</div>
        {/if}
    </div>

    {#if content || link}
        <HeroText
            {mastheadTextColour}
            {content}
            {subContent}
            {link}
            {linkText}
            {link2}
            {linkText2}
            {inverseButtonColours}
            {headingLevel}
            {titleSize}
        />
    {/if}
</div>

<style lang="scss">
    @use '/src/scss/global-constants/mq-config';
    @use 'sass-mq/mq' as *;

    .herovideo {
        aspect-ratio: 27 / 43;
        overflow: hidden;
        position: relative;
        width: 100%;

        @include mq($from: tablet) {
            aspect-ratio: 19 / 8;
        }

    }

    .herovideo :global(.container--video-player) {
        height: 100%;
        inset: 0;
        opacity: 0;
        position: absolute;
        transition: opacity 450ms ease;
        width: 100%;
        z-index: 1;
    }

    .herovideo[data-video-ready="true"] :global(.container--video-player) {
        opacity: 1;
    }

    :global(.mobile-only) {
        aspect-ratio: 27/43;
        
        @include mq($from: tablet) {
            display: none !important;
        }
    }

    :global(.desktop-only) {
        display: none !important;
        aspect-ratio: 19/8;

        @include mq($from: tablet) {
            display: flex !important;
        }
    }

    .video-loading {
        align-items: center;
        background: #f0f0f0;
        display: flex;
        height: 100%;
        justify-content: center;
        left: 0;
        opacity: 1;
        pointer-events: none;
        position: absolute;
        top: 0;
        transition: opacity 450ms ease;
        width: 100%;
        z-index: 2;
        will-change: opacity;

        &[data-hidden="true"] {
            opacity: 0;
        }

        .video-placeholder {
            color: #666;
            font-size: 1.4rem;
        }

        :global(picture),
        :global(img) {
            display: block;
            height: 100%;
            width: 100%;
        }

        :global(img) {
            object-fit: cover;
        }
    }

    :global(.hero-text) {
        z-index: 3;
    }

</style>
